import { useLocalParticipant, useRoomContext, useSpeakingParticipants} from "@livekit/components-react";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ArrowDown } from "lucide-react";
import { useTranslate } from '@tolgee/react';
import ParticipantComponentFocusedTranslationEnabled from "./ParticipantComponentFocusedTranslationEnabled";
import ParticipantComponentTranslationEnabled from "./ParticipantComponentsTranslationEnabled";
import ParticipantComponent from "./ParticipantComponent";
import { calculateGalleryColumns } from "../../Helpers/callHelper/galleryColumnsHelper";
import { useElementSize } from "../../Helpers/callHelper/useElementSize";
import Pill from "../GenericComponents/Pill";
import { colors, rgb } from "../../Assets/styles/colors";


const ParticipantsWrapperTranslationGrid = ({ callInfo, participants, grid, speakersQueue }) => {
  const room = useRoomContext();
  const localParticipant = useLocalParticipant();
  const { t } = useTranslate();
  const [speakingParticipant, setSpeakingParticipant] = useState(
    localParticipant.localParticipant
  );
  const filteredParticipantsRef = useRef(participants);
  const [filteredParticipants, setFilteredParticipants] = useState(
    filteredParticipantsRef.current
  );

  const { ref: galleryRef, width: galleryWidth, height: galleryHeight } = useElementSize();
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const updateHasMoreBelow = () => {
    const el = galleryRef.current;
    if (!el) {
      setHasMoreBelow(false);
      return;
    }
    const remainingPx = el.scrollHeight - el.scrollTop - el.clientHeight;
    setHasMoreBelow(remainingPx > 4);
  };

  const activeSpeakers = useSpeakingParticipants();

  useEffect(() => {
    if (grid && activeSpeakers.length > 0) {
       const loudestSpeakerIdentity = activeSpeakers[0]?.identity;

       const matchedParticipant = participants.find(
          (p) => p.identity === loudestSpeakerIdentity
       );

       if (matchedParticipant && speakingParticipant?.identity !== matchedParticipant.identity) {
          setSpeakingParticipant(matchedParticipant);
       }
    }
  }, [activeSpeakers, grid, speakingParticipant, participants]);

  const columns = calculateGalleryColumns(participants.length, galleryWidth, galleryHeight);

  useEffect(() => {
    let filteredparticipants = participants.filter(
      (p) => p.identity !== speakingParticipant.identity
    );
    filteredParticipantsRef.current = filteredparticipants;
    setFilteredParticipants(filteredparticipants);
  }, [speakingParticipant, participants]);

  useEffect(() => {
    updateHasMoreBelow();
  }, [participants, columns, galleryWidth, galleryHeight]);

  return (
    <Wrapper grid={grid}>
      {!grid && (
        <GridSection>
          <GridViewContainer ref={galleryRef} columns={columns} onScroll={updateHasMoreBelow}>
            {participants.map((participant, index) => (
              <ParticipantComponent
                participant={participant}
                callInfo={callInfo}
                key={`${participant.identity}-${index}`}
                speakersQueue={speakersQueue}
                compact
              />
            ))}
          </GridViewContainer>
          {hasMoreBelow && (
            <Pill
              size="md"
              color={colors.speakingGreen}
              $borderColor={`rgba(${rgb(colors.speakingGreen)}, 0.4)`}
              $bottom={12}
              $centerX
            >
              {t('call_modal.scroll_for_more')}
              <ArrowDown size={14} />
            </Pill>
          )}
        </GridSection>
      )}
      {grid &&
        <FocusViewContainer>
          <FocusWrapper>
            <ParticipantComponentFocusedTranslationEnabled
              participant={speakingParticipant}
              callInfo={callInfo}
              key={speakingParticipant.identity}
              grid={false}
              focused={true}
              speakersQueue={speakersQueue}
            />
          </FocusWrapper>
          <GridViewContainer columns={2} grid={grid}>
            {filteredParticipants.map((participant) => (
              <ParticipantComponentTranslationEnabled
                participant={participant}
                callInfo={callInfo}
                key={participant.identity}
                speakersQueue={speakersQueue}
              />
            ))}
          </GridViewContainer>
        </FocusViewContainer>
      }

    </Wrapper>
  );
};

export default ParticipantsWrapperTranslationGrid;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: auto;
max-width:${({ grid }) => grid ? "900px" : "unset"};
`;

const GridSection = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
const GridViewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  grid-auto-rows: 1fr;
  width: 100%;
  height: 100%;
  gap: 10px;
  overflow: ${({ grid }) => grid ? "unset" : "auto"};


`;
const FocusViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
  gap: 20px;
  padding: 5px;
`;

const FocusWrapper = styled.div`
  display: flex;
  width: 100%;
  height:100%;
  min-height: 500px;
  overflow: hidden;
`;
